import React from 'react'

import { useFloatingElement } from 'hooks'

const useTooltipState = ({ delay = 100, isInitiallyToggled = false } = {}) => {
  const [isToggled, setIsToggled] = React.useState(isInitiallyToggled)

  const isTouchEvent = React.useRef(false)

  const timeoutId = React.useRef(null)

  const handleClose = React.useCallback(() => {
    window.clearTimeout(timeoutId.current)

    timeoutId.current = window.setTimeout(() => {
      setIsToggled(false)
    }, delay)
  }, [delay, timeoutId, setIsToggled])

  const handleOpen = React.useCallback(() => {
    window.clearTimeout(timeoutId.current)

    timeoutId.current = window.setTimeout(() => {
      setIsToggled(true)
    }, delay)
  }, [delay, timeoutId, setIsToggled])

  const handleBlur = React.useCallback(() => {
    if (isTouchEvent.current) return

    handleClose()
  }, [handleClose, isTouchEvent])

  const handleFocus = React.useCallback(() => {
    if (isTouchEvent.current) return

    handleOpen()
  }, [handleOpen, isTouchEvent])

  const handleMouseEnter = React.useCallback(() => {
    if (isTouchEvent.current) return

    handleOpen()
  }, [handleOpen, isTouchEvent])

  const handleMouseLeave = React.useCallback(() => {
    if (isTouchEvent.current) return

    handleClose()
  }, [handleClose, isTouchEvent])

  const handleTouchStart = React.useCallback(
    event => {
      if (event.type === 'touchstart') {
        isTouchEvent.current = true
        // TODO: maybe we have to investigate what it does
        document.body.style.webkitUserSelect = 'none'
      }

      handleOpen()
    },
    [handleOpen],
  )

  const handleTouchEnd = React.useCallback(handleClose, [handleClose])

  React.useEffect(() => {
    const handleEscapeKey = event => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        handleMouseLeave()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)

    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [handleMouseLeave])

  React.useEffect(() => {
    return () => {
      window.clearTimeout(timeoutId.current)
    }
  }, [timeoutId])

  return {
    state: { isToggled },
    handlers: {
      handleBlur,
      handleFocus,
      handleMouseEnter,
      handleMouseLeave,
      handleTouchEnd,
      handleTouchStart,
    },
  }
}

export const useTooltip = ({
  initialPlacement = 'top' as const,
  offsetSize = 9.5,
  hasArrow = true,
} = {}) => {
  const {
    arrowRef,
    arrowStyle,
    floating,
    floatingStyle,
    reference,
  } = useFloatingElement({ initialPlacement, offsetSize, hasArrow })

  const {
    state: { isToggled },
    handlers: {
      handleBlur,
      handleFocus,
      handleMouseEnter,
      handleMouseLeave,
      handleTouchEnd,
      handleTouchStart,
    },
  } = useTooltipState()

  return {
    state: {
      arrowRef,
      arrowStyle,
      floating,
      floatingStyle,
      reference,
      hasArrow,
      isToggled,
    },
    handlers: {
      handleBlur,
      handleFocus,
      handleMouseEnter,
      handleMouseLeave,
      handleTouchEnd,
      handleTouchStart,
    },
  }
}
